import { Box, Button, CloseButton, HStack, Text, VStack } from '@chakra-ui/react';
import { MaterialIcon } from './MaterialIcon';

interface ProactiveToastProps {
  /** The AI insight or suggestion message */
  message: string;
  /** Optional action button label (e.g. "View Note") */
  actionLabel?: string;
  /** Callback when the action button is clicked */
  onAction?: () => void;
  /** Callback to dismiss the toast — required since it does not auto-dismiss */
  onClose: () => void;
  /** Header glyph + label; Bower's sparkle by default. */
  heading?: { icon: string; label: string };
}

/**
 * Proactive AI toast — used for Bird's QA insights on OCR / voice notes.
 *
 * Differs from ActionToast:
 *  - Card-style appearance with brand accent (not a Chakra Alert)
 *  - Shows "Bird" branding with sparkle icon
 *  - Does NOT auto-dismiss — user must close it
 *  - Optional action button to navigate to the relevant note
 */
export function ProactiveToast({
  message,
  actionLabel,
  onAction,
  onClose,
  heading = { icon: 'auto_awesome', label: 'Bower' },
}: ProactiveToastProps) {
  return (
    <Box
      bg="bg.surface"
      borderRadius="xl"
      boxShadow="lg"
      borderLeft="4px solid"
      borderColor="brand.primary"
      p={4}
      maxW="sm"
    >
      <HStack justify="space-between" align="start" spacing={2}>
        <VStack align="start" spacing={2} flex={1}>
          {/* Bower branding */}
          <HStack spacing={1.5}>
            <MaterialIcon name={heading.icon} boxSize={4} color="brand.onSurface" />
            <Text fontSize="sm" fontWeight="semibold" color="text.primary">
              {heading.label}
            </Text>
          </HStack>

          {/* Message */}
          <Text fontSize="sm" color="text.secondary" lineHeight="1.5">
            {message}
          </Text>

          {/* Optional action button */}
          {actionLabel && onAction && (
            <Button
              size="xs"
              variant="outline"
              onClick={() => {
                onAction();
                onClose();
              }}
            >
              {actionLabel}
            </Button>
          )}
        </VStack>

        <CloseButton size="sm" onClick={onClose} color="text.muted" />
      </HStack>
    </Box>
  );
}
